import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import { ErrorComponent } from "core/components/ErrorComponent";
import { StyledTabs } from "core/components/StyledTabs";
import type { ItemType } from "core/entities";
import { useBreakpoints } from "core/hooks/useBreakpoints";
import { usePageLogging } from "core/hooks/usePageLogging";
import { MyLinksIcon } from "features/MyLinks/components/MyLinksIcon";
import Head from "next/head";
import { useRouter } from "next/router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { SearchResult } from "./defs";
import { SearchItem } from "./SearchItem/SearchItem";
import { useGlobalSearch } from "./useGlobalSearch";

type TabValue = Exclude<ItemType, "External"> | "All";
const tabValues: TabValue[] = [
  "All",
  "DmsDocNodeLibrary",
  "DmsDocNodeDocument",
  "News",
  "Event",
];
function Wrapper({
  children,
  tabValue,
  onTabChange,
  filterText,
}: {
  children: React.ReactNode;
  tabValue: TabValue;
  onTabChange: (newValue: TabValue) => void;
  filterText?: string;
}) {
  const { t } = useTranslation("Fixhub", { keyPrefix: "Next:Core:SearchPage" });
  const handleTabChange = (_: React.SyntheticEvent, newValue: TabValue) => {
    onTabChange(newValue);
  };
  const { isMobile } = useBreakpoints();
  return (
    <>
      <Head>
        <title>{t("headTitle", { filterText })}</title>
      </Head>
      <Container maxWidth="xl">
        <Typography variant="h3" component="h1" color="primary" mb={3}>
          {t("title", {
            filterText,
          })}
        </Typography>
        <StyledTabs
          value={tabValue}
          variant="scrollable"
          onChange={handleTabChange}
        >
          {tabValues.map((val) => {
            const displayTabText =
              !isMobile || val === "All" || val === tabValue;
            return (
              <Tab
                key={val}
                value={val}
                label={
                  <Stack direction="row" alignItems="center" gap={1}>
                    {val !== "All" && <MyLinksIcon itemType={val} />}
                    {displayTabText && t(val)}
                  </Stack>
                }
              />
            );
          })}
        </StyledTabs>
        <Divider sx={{ mb: 4 }} />
        {children}
      </Container>
    </>
  );
}

export function SearchPage() {
  usePageLogging({
    itemType: "Page",
    itemId: "search",
    componentName: "SearchPage",
  });
  const { t } = useTranslation("Fixhub", { keyPrefix: "Next:Core:SearchPage" });
  const [tabValue, setTabValue] = useState<TabValue>("All");
  const {
    query: { filterText: queryFilterText },
  } = useRouter();

  const filterText = String(queryFilterText);
  const {
    items,
    totalCount,
    currentCount,
    showLoadMore,
    loadMore,
    isLoadingMore,
    isLoadingInitialData,
    isError,
  } = useGlobalSearch(filterText, tabValue === "All" ? undefined : tabValue);

  if (isError) {
    return (
      <Wrapper
        tabValue={tabValue}
        onTabChange={setTabValue}
        filterText={filterText}
      >
        <ErrorComponent apiError={isError} />
      </Wrapper>
    );
  }
  if (isLoadingInitialData) {
    return (
      <Wrapper
        tabValue={tabValue}
        onTabChange={setTabValue}
        filterText={filterText}
      >
        <LinearProgress />
      </Wrapper>
    );
  }
  if (items.length === 0) {
    const itemTypeText = tabValue === "All" ? t("items") : t(tabValue);
    return (
      <Wrapper
        tabValue={tabValue}
        onTabChange={setTabValue}
        filterText={filterText}
      >
        <Typography variant="h5" sx={{ px: 2 }}>
          {t("noEntities", {
            itemType: itemTypeText,
          })}
        </Typography>
      </Wrapper>
    );
  }

  return (
    <Wrapper
      tabValue={tabValue}
      onTabChange={setTabValue}
      filterText={filterText}
    >
      <Typography variant="subtitle1" color="text.secondary" mb={2}>
        {t("listTitle", {
          currentCount,
          totalCount,
        })}
      </Typography>
      {items.map((searchItemProps) => (
        <SearchItem
          key={searchItemProps.id}
          {...(searchItemProps as SearchResult)}
        />
      ))}
      {showLoadMore && (
        <Box display="flex" justifyContent="center">
          <Button
            variant="outlined"
            startIcon={<ExpandMoreIcon />}
            loading={isLoadingMore}
            onClick={loadMore}
          >
            {t("showMore")}
          </Button>
        </Box>
      )}
    </Wrapper>
  );
}
